const SESSION_ID_KEY = 'iaAgencyIdentitySessionId';
const PREVIOUS_PAGE_NAME_KEY = 'iaAgencyIdentityPreviousPageName';

let lastPageLoad = { pageName: '', previousPageName: '' };

export const GtmEventType = Object.freeze({
    PageLoad: 'PageLoad',
    PageExit: 'PageExit',
    ButtonClick: 'ButtonClick',
    LinkClick: 'LinkClick',
    RadioChange: 'RadioChange',
    CheckboxChange: 'CheckboxChange'
});

// Resolves the current and previous page names, advancing the stored "previous page" pointer.
// Called on page load/exit so navigation is recorded exactly once per page.
export function resolvePageNames(pageName) {
    const storedPageName = window.sessionStorage?.getItem(PREVIOUS_PAGE_NAME_KEY) || '';

    // A second tracking call on the same page must not report the page as its own predecessor.
    if (storedPageName === pageName && lastPageLoad.pageName === pageName) {
        return lastPageLoad;
    }

    if (storedPageName !== pageName) {
        window.sessionStorage?.setItem(PREVIOUS_PAGE_NAME_KEY, pageName);
    }
    lastPageLoad = { pageName, previousPageName: storedPageName };
    return lastPageLoad;
}

// Read-only accessor for the current page's previous page name (no side effects),
// so interaction events can report the same previousPageName as the page load.
export function getPreviousPageName(pageName) {
    return lastPageLoad.pageName === pageName ? lastPageLoad.previousPageName : '';
}

export function generateGuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for environments without randomUUID: build an RFC 4122 v4 UUID
    // from a cryptographically secure source rather than Math.random().
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
}

// Onboarding users are anonymous, so use a session id that is stable per browser tab.
export function getAgencyIdentitySessionId() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return '';
    }
    let sessionId = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
        sessionId = generateGuid();
        window.sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
}

// Builds the GTM metadata shared by every event. Event-specific fields
// (previousPageName, pageLoadLatency, timeOnPage, click IDs, etc.) are passed as extra.
export function buildGtmEventMetadata({
    pageName,
    pageViewId,
    eventType,
    agentInterviewId,
    agencyInterviewId,
    ...extra
}) {
    return {
        pageName,
        pageViewId,
        eventType,
        agentInterviewId,
        agencyInterviewId,
        iaAgencyIdentitySessionId: getAgencyIdentitySessionId(),
        ...extra
    };
}

export function trackGtmEvent(eventName, metadata) {
    if (!window._gtm) {
        return false;
    }
    try {
        // VariableAction reads geicoDtm. Remove fields belonging to the preceding event type.
        const context = window.geicoDtm || {};
        for (const key of ['buttonId', 'linkId', 'targetURL', 'radioId', 'selectedRadioId', 'previouslySelectedRadioId', 'checkboxId', 'checked', 'timeOnPage']) {
            delete context[key];
        }
        const now = new Date();
        const eventMetadata = {
            ...metadata,
            language: window.navigator.language,
            eventTimestampEpochMs: now.getTime(),
            eventTimestampUtc: now.toISOString()
        };
        window.geicoDtm = Object.assign(context, eventMetadata);
        window._gtm.track(eventName, eventMetadata);
    } catch (error) {
        console.error('Error tracking GTM event:', error);
    }
    return true;
}
